/**
 * On-demand comic page image loader: resolves base64 bytes by imageId through
 * memory LRU -> persistent cache -> server, keeping only a bounded set resident.
 */

import type { Socket } from "socket.io-client";
import type { ServerToClientEvents, ClientToServerEvents } from "@amzn/a4k-creator-studio-shared";
import { getCachedImage, setCachedImage } from "./imageCache";

type TypedSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

const MEMORY_CAPACITY = 10;
const RETRY_MS = 15000;
const MAX_ATTEMPTS = 3;
// Must not exceed the server's appConfig.images.fetchLimit — it truncates larger batches.
const FETCH_BATCH = 50;

const memory = new Map<string, string>();
const listeners = new Map<string, Set<(base64: string) => void>>();
const inflight = new Map<string, ReturnType<typeof setTimeout>>();
const attempts = new Map<string, number>();
let queued = new Set<string>();
let flushScheduled = false;
let socket: TypedSocket | null = null;

export function setImageSocket(s: TypedSocket | null): void {
  socket = s;
  if (s && queued.size > 0) scheduleFlush();
}

export function isAwaited(id: string): boolean {
  return listeners.has(id);
}

function touch(id: string, base64: string): void {
  if (memory.has(id)) memory.delete(id);
  memory.set(id, base64);
  while (memory.size > MEMORY_CAPACITY) {
    const oldest = memory.keys().next().value as string;
    memory.delete(oldest);
  }
}

function clearInflight(id: string): void {
  const timer = inflight.get(id);
  if (timer) clearTimeout(timer);
  inflight.delete(id);
  attempts.delete(id);
}

function notify(id: string, base64: string): void {
  const set = listeners.get(id);
  if (set) for (const cb of [...set]) cb(base64);
}

export function peekImage(id: string | undefined): string | null {
  if (!id) return null;
  const mem = memory.get(id);
  return mem === undefined ? null : mem;
}

export function ingestImage(id: string, base64: string): void {
  touch(id, base64);
  setCachedImage(id, base64).catch(() => {});
  clearInflight(id);
  notify(id, base64);
}

function scheduleFlush(): void {
  if (flushScheduled) return;
  flushScheduled = true;
  Promise.resolve().then(() => {
    flushScheduled = false;
    if (queued.size === 0 || !socket) return;
    const batch = [...queued];
    queued = new Set();
    for (let i = 0; i < batch.length; i += FETCH_BATCH) {
      socket.emit("fetchImages", { imageIds: batch.slice(i, i + FETCH_BATCH) });
    }
  });
}

function requestImage(id: string): void {
  if (inflight.has(id)) return;
  const attempt = (attempts.get(id) ?? 0) + 1;
  attempts.set(id, attempt);
  inflight.set(id, setTimeout(() => {
    inflight.delete(id);
    if (listeners.has(id) && memory.get(id) === undefined && attempt < MAX_ATTEMPTS) {
      requestImage(id);
    }
  }, RETRY_MS));

  getCachedImage(id)
    .then((cached) => {
      if (cached) {
        touch(id, cached);
        clearInflight(id);
        notify(id, cached);
      } else {
        queued.add(id);
        scheduleFlush();
      }
    })
    .catch(() => {
      queued.add(id);
      scheduleFlush();
    });
}

export function subscribeImage(id: string, cb: (base64: string) => void): () => void {
  let set = listeners.get(id);
  if (!set) {
    set = new Set();
    listeners.set(id, set);
  }
  set.add(cb);

  const mem = memory.get(id);
  if (mem !== undefined) {
    touch(id, mem);
    cb(mem);
  } else {
    requestImage(id);
  }

  return () => {
    const s = listeners.get(id);
    if (!s) return;
    s.delete(cb);
    if (s.size === 0) {
      listeners.delete(id);
      clearInflight(id);
    }
  };
}
